import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const CONSENT_KEY = 'sakk_cookie_consent'

export type ConsentPreferences = {
  necessary: boolean
  analytics: boolean
  marketing: boolean
  timestamp: string
}

export function getConsent(): ConsentPreferences | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function setConsent(prefs: ConsentPreferences) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs))
  window.dispatchEvent(new Event('consent-updated'))
}

export function CookieConsent() {
  const { t } = useTranslation()
  const [show, setShow] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [analytics, setAnalytics] = useState(true)
  const [marketing, setMarketing] = useState(true)

  useEffect(() => {
    if (!getConsent()) {
      const timer = setTimeout(() => setShow(true), 1000)
      return () => clearTimeout(timer)
    }
  }, [])

  if (!show) return null

  const acceptAll = () => {
    setConsent({ necessary: true, analytics: true, marketing: true, timestamp: new Date().toISOString() })
    setShow(false)
  }

  const acceptSelected = () => {
    setConsent({ necessary: true, analytics, marketing, timestamp: new Date().toISOString() })
    setShow(false)
  }

  const rejectAll = () => {
    setConsent({ necessary: true, analytics: false, marketing: false, timestamp: new Date().toISOString() })
    setShow(false)
  }

  return (
    <div
      className="cc-banner"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        padding: '16px',
        animation: 'cc-slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      <style>{`
        @keyframes cc-slide-up {
          from { opacity: 0; transform: translateY(100%); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div
        style={{
          maxWidth: '48rem',
          margin: '0 auto',
          borderRadius: '1rem',
          backgroundColor: '#fff',
          border: '1px solid #F2ECE5',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                backgroundColor: '#F8F0F1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: 2,
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#85001B" style={{ width: 20, height: 20 }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#261419' }}>{t('cookie.title')}</h3>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6C5A5F', lineHeight: 1.6 }}>{t('cookie.description')}</p>
            </div>
          </div>

          {/* Details toggle */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            style={{
              marginTop: 12,
              fontSize: 12,
              fontWeight: 600,
              color: '#85001B',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: 0,
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" style={{ width: 12, height: 12, transition: 'transform 0.2s', transform: showDetails ? 'rotate(90deg)' : 'none' }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
            {t('cookie.customize')}
          </button>

          {/* Details panel */}
          {showDetails && (
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Necessary */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: 12, borderRadius: 12, backgroundColor: '#F8F4F1' }}>
                <div>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#261419' }}>{t('cookie.necessary')}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: '#6C5A5F' }}>{t('cookie.necessaryDesc')}</p>
                </div>
                <div style={{ flexShrink: 0, width: 36, height: 20, borderRadius: 10, backgroundColor: '#85001B', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 2px' }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }} />
                </div>
              </div>
              {/* Analytics */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: 12, borderRadius: 12, backgroundColor: '#F8F4F1' }}>
                <div>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#261419' }}>{t('cookie.analytics')}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: '#6C5A5F' }}>{t('cookie.analyticsDesc')}</p>
                </div>
                <button
                  onClick={() => setAnalytics(!analytics)}
                  style={{
                    flexShrink: 0,
                    width: 36,
                    height: 20,
                    borderRadius: 10,
                    backgroundColor: analytics ? '#85001B' : 'rgba(38,20,25,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: analytics ? 'flex-end' : 'flex-start',
                    padding: '0 2px',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }} />
                </button>
              </div>
              {/* Marketing */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: 12, borderRadius: 12, backgroundColor: '#F8F4F1' }}>
                <div>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#261419' }}>{t('cookie.marketing')}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: '#6C5A5F' }}>{t('cookie.marketingDesc')}</p>
                </div>
                <button
                  onClick={() => setMarketing(!marketing)}
                  style={{
                    flexShrink: 0,
                    width: 36,
                    height: 20,
                    borderRadius: 10,
                    backgroundColor: marketing ? '#85001B' : 'rgba(38,20,25,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: marketing ? 'flex-end' : 'flex-start',
                    padding: '0 2px',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div
          style={{
            padding: '16px 24px',
            backgroundColor: 'rgba(247,243,238,0.5)',
            borderTop: '1px solid rgba(242,236,229,0.5)',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Link
            to="/privacy"
            style={{ fontSize: 11, color: '#A99CA0', textDecoration: 'none' }}
          >
            {t('cookie.privacyPolicy')}
          </Link>
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={rejectAll}
              style={{
                padding: '10px 16px',
                borderRadius: 12,
                fontSize: 12,
                fontWeight: 600,
                color: '#6C5A5F',
                backgroundColor: '#F8F4F1',
                border: '1px solid #F2ECE5',
                cursor: 'pointer',
              }}
            >
              {t('cookie.rejectAll')}
            </button>
            {showDetails && (
              <button
                onClick={acceptSelected}
                style={{
                  padding: '10px 16px',
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#85001B',
                  backgroundColor: '#F8F0F1',
                  border: '1px solid rgba(228,196,204,0.5)',
                  cursor: 'pointer',
                }}
              >
                {t('cookie.saveSelection')}
              </button>
            )}
            <button
              onClick={acceptAll}
              style={{
                padding: '10px 20px',
                borderRadius: 12,
                fontSize: 12,
                fontWeight: 700,
                color: '#fff',
                backgroundColor: '#85001B',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 4px 6px -1px rgba(104,0,21,0.1)',
              }}
            >
              {t('cookie.acceptAll')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
